/**
 * /agents/settings — operator reference. The one-time Google Workspace
 * Domain-Wide Delegation (DWD) grant the mailbox-scanning agents need before
 * they can be turned on, plus the model-provider config keys.
 *
 * Also hosts the Agent Studio admin panels (connector approvals, studio
 * access manager, usage) — rendered ONLY when the signed-in SuperAdmin also
 * has agentStudioEnabled, so the studio stays invisible to everyone else.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  List,
  ListItem,
  ListIcon,
  Code,
  Divider,
  Alert,
  AlertIcon,
  Link,
  useColorModeValue,
  Button,
  Input,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  useToast,
} from '@chakra-ui/react';
import { FiCheckCircle, FiExternalLink, FiCheck, FiX, FiUserPlus, FiUserMinus } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { callAgentStudio } from '../../hooks/useAssistants';

const SCOPES = [
  { scope: 'https://www.googleapis.com/auth/gmail.readonly', why: 'Scan inbox messages' },
  { scope: 'https://www.googleapis.com/auth/gmail.modify', why: 'Label, quarantine, and trash threats' },
  { scope: 'https://www.googleapis.com/auth/admin.directory.user.readonly', why: 'Enumerate mailboxes (already used elsewhere)' },
];

const connectorStatusColor = { pending: 'yellow', approved: 'green', rejected: 'red' };

/**
 * Agent Studio admin panels. Mounted only for flag-holding SuperAdmins (the
 * parent checks both), so other admins never see the studio exists.
 */
function StudioAdminPanels({ cardBg, cardBorder, subtle }) {
  const toast = useToast();
  const [connectors, setConnectors] = useState([]);
  const [studioUsers, setStudioUsers] = useState([]);
  const [usage, setUsage] = useState(null);
  const [grantEmail, setGrantEmail] = useState('');
  const [secretRefs, setSecretRefs] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [conns, users, report] = await Promise.all([
        callAgentStudio('listConnectors'),
        callAgentStudio('listStudioUsers'),
        callAgentStudio('usageReport', { days: 7 }),
      ]);
      setConnectors(conns.connectors || []);
      setStudioUsers(users.users || []);
      setUsage(report);
    } catch (err) {
      toast({ title: 'Studio admin load failed', description: err.message, status: 'error', duration: 6000 });
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const resolveConnector = async (connector, approved) => {
    setBusy(true);
    try {
      await callAgentStudio(approved ? 'approveConnector' : 'rejectConnector', {
        id: connector.id,
        ...(approved && secretRefs[connector.id] ? { secretRef: secretRefs[connector.id] } : {}),
      });
      toast({ title: `${connector.label} ${approved ? 'approved' : 'rejected'}`, status: approved ? 'success' : 'info', duration: 3000 });
      load();
    } catch (err) {
      toast({ title: 'Action failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setBusy(false);
    }
  };

  const grant = async (email, enabled) => {
    if (!email) return;
    setBusy(true);
    try {
      await callAgentStudio(enabled ? 'grantStudioAccess' : 'revokeStudioAccess', { email });
      toast({ title: `${email} ${enabled ? 'granted' : 'revoked'}`, status: 'success', duration: 3000 });
      setGrantEmail('');
      load();
    } catch (err) {
      toast({ title: 'Access change failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setBusy(false);
    }
  };

  const pending = connectors.filter((c) => c.status === 'pending');

  return (
    <VStack align="stretch" spacing={6} mt={6}>
      <Divider />
      <Heading size="md">Agent Studio admin</Heading>

      {/* ------------------------------------------------ connector approvals */}
      <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={5}>
        <Heading size="sm" mb={1}>Connector approvals</Heading>
        <Text fontSize="xs" color={subtle} mb={3}>
          Custom MCP connectors users requested. Set the token first (<Code fontSize="xs">firebase functions:config:set group.key=&quot;TOKEN&quot;</Code>),
          then approve with that <Code fontSize="xs">group.key</Code> as the secret ref. Tokens never live in the database.
        </Text>
        {connectors.length === 0 ? (
          <Text fontSize="sm" color={subtle}>No connectors yet.</Text>
        ) : (
          <Table size="sm">
            <Thead><Tr><Th>Connector</Th><Th>Endpoint</Th><Th>Status</Th><Th>Secret ref</Th><Th /></Tr></Thead>
            <Tbody>
              {connectors.map((c) => (
                <Tr key={c.id}>
                  <Td fontSize="sm"><b>{c.label}</b><Text fontSize="xs" color={subtle}>{c.key} · {c.kind}</Text></Td>
                  <Td fontSize="xs" maxW="220px"><Text noOfLines={1}>{c.manifest?.url || '(builtin)'}</Text></Td>
                  <Td><Tag size="sm" colorScheme={connectorStatusColor[c.status] || 'gray'}>{c.status}</Tag></Td>
                  <Td>
                    {c.status === 'pending' && c.kind === 'mcp' ? (
                      <Input
                        size="xs" w="150px" placeholder="agentmcp.key"
                        value={secretRefs[c.id] || ''}
                        onChange={(e) => setSecretRefs({ ...secretRefs, [c.id]: e.target.value })}
                      />
                    ) : (
                      <Code fontSize="xs">{c.secret_ref || '—'}</Code>
                    )}
                  </Td>
                  <Td textAlign="right">
                    {c.status === 'pending' && (
                      <HStack justify="flex-end" spacing={1}>
                        <Button size="xs" colorScheme="green" leftIcon={<FiCheck />} onClick={() => resolveConnector(c, true)} isLoading={busy}>Approve</Button>
                        <Button size="xs" variant="outline" colorScheme="red" leftIcon={<FiX />} onClick={() => resolveConnector(c, false)} isDisabled={busy}>Reject</Button>
                      </HStack>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
        {pending.length > 0 && (
          <Alert status="warning" borderRadius="md" mt={3} fontSize="xs"><AlertIcon />{pending.length} connector request(s) awaiting your review.</Alert>
        )}
      </Box>

      {/* ---------------------------------------------------- access manager */}
      <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={5}>
        <Heading size="sm" mb={1}>Studio access</Heading>
        <Text fontSize="xs" color={subtle} mb={3}>
          Who can see /assistants and use the callables. The flag is per-user (<Code fontSize="xs">agentStudioEnabled</Code>) — SuperAdmin does NOT bypass it.
        </Text>
        <HStack mb={3}>
          <Input size="sm" placeholder="user@future.loans" value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} maxW="300px" />
          <Button size="sm" leftIcon={<FiUserPlus />} colorScheme="blue" onClick={() => grant(grantEmail.trim(), true)} isLoading={busy}>Grant</Button>
        </HStack>
        {studioUsers.length === 0 ? (
          <Text fontSize="sm" color={subtle}>Nobody has access yet.</Text>
        ) : (
          <VStack align="stretch" spacing={1}>
            {studioUsers.map((u) => (
              <HStack key={u.uid} justify="space-between" borderWidth="1px" borderColor={cardBorder} borderRadius="md" px={3} py={1.5}>
                <Text fontSize="sm">{u.name || u.uid} <Text as="span" fontSize="xs" color={subtle}>{u.email}</Text></Text>
                <Button size="xs" variant="ghost" colorScheme="red" leftIcon={<FiUserMinus />} onClick={() => grant(u.email, false)} isDisabled={busy || !u.email}>
                  Revoke
                </Button>
              </HStack>
            ))}
          </VStack>
        )}
      </Box>

      {/* -------------------------------------------------------------- usage */}
      <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={5}>
        <Heading size="sm" mb={1}>Usage (last 7 days)</Heading>
        {!usage ? (
          <Text fontSize="sm" color={subtle}>Loading…</Text>
        ) : (
          <>
            <Text fontSize="xs" color={subtle} mb={3}>
              {usage.totalMessages} messages · ~${(usage.totalCostUsd || 0).toFixed(2)} estimated model spend.
            </Text>
            {usage.users?.length > 0 && (
              <Table size="sm">
                <Thead><Tr><Th>User</Th><Th isNumeric>Messages</Th><Th isNumeric>Est. cost</Th></Tr></Thead>
                <Tbody>
                  {usage.users.map((u) => (
                    <Tr key={u.userId}>
                      <Td fontSize="xs">{u.userId}</Td>
                      <Td isNumeric fontSize="xs">{u.messages}</Td>
                      <Td isNumeric fontSize="xs">${u.costUsd.toFixed(4)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </>
        )}
      </Box>
    </VStack>
  );
}

export default function AgentsSettingsPage() {
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const subtle = useColorModeValue('gray.600', 'gray.400');
  const codeBg = useColorModeValue('gray.50', 'gray.900');
  const { currentUser } = useAuth();
  // Studio panels stay invisible unless this SuperAdmin ALSO holds the studio
  // flag — other admins must not learn the studio exists.
  const showStudioAdmin = currentUser?.agentStudioEnabled === true;

  return (
    <Box p={6}>
      <Heading size="md" mb={1}>Settings &amp; prerequisites</Heading>
      <Text fontSize="sm" color={subtle} mb={5}>What must be in place before enabling mailbox-scanning agents.</Text>

      <Alert status="warning" borderRadius="md" mb={6}>
        <AlertIcon />
        <Box fontSize="sm">
          Mailbox-scanning agents read every employee&apos;s inbox via service-account Domain-Wide Delegation. The scopes
          below must be granted to the DWD client in the Google Admin Console <b>before</b> the Phishing Monitor can run.
        </Box>
      </Alert>

      <VStack align="stretch" spacing={6}>
        <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={5}>
          <Heading size="sm" mb={3}>1. Grant Gmail scopes (one-time)</Heading>
          <Text fontSize="sm" color={subtle} mb={3}>
            In <b>Google Admin Console → Security → Access and data control → API controls → Domain-wide delegation</b>,
            edit the Hub&apos;s service-account client (the same one used for Gmail send / signatures — its client email is in
            <Code mx={1}>functions.config().google.client_email</Code>) and ensure it authorizes these scopes for all users:
          </Text>
          <Box bg={codeBg} borderRadius="md" p={3}>
            <List spacing={2}>
              {SCOPES.map((s) => (
                <ListItem key={s.scope} fontSize="sm">
                  <ListIcon as={FiCheckCircle} color="green.400" />
                  <Code fontSize="xs">{s.scope}</Code>
                  <Text as="span" color={subtle} fontSize="xs"> — {s.why}</Text>
                </ListItem>
              ))}
            </List>
          </Box>
          <HStack mt={3}>
            <Link href="https://admin.google.com/ac/owl/domainwidedelegation" isExternal fontSize="sm" color="blue.400">
              Open Domain-wide delegation <FiExternalLink style={{ display: 'inline' }} />
            </Link>
          </HStack>
          <Text fontSize="xs" color={subtle} mt={2}>
            Tip: copy the client&apos;s numeric <b>Client ID</b> (OAuth2 client ID of the service account) into the
            &quot;Client ID&quot; field when adding scopes. Today only <Code fontSize="xs">gmail.send</Code> +
            <Code fontSize="xs" ml={1}>gmail.settings.basic</Code> are formally documented on it — add the three above.
          </Text>
        </Box>

        <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={5}>
          <Heading size="sm" mb={3}>2. Model providers</Heading>
          <Text fontSize="sm" color={subtle} mb={2}>Agents can use either provider per-agent (set in the agent&apos;s config):</Text>
          <List spacing={2} fontSize="sm">
            <ListItem><ListIcon as={FiCheckCircle} color="green.400" />Gemini — <Code fontSize="xs">functions.config().google.gemini_api_key</Code> or Vertex (<Code fontSize="xs">futurebot.use_vertex=true</Code>)</ListItem>
            <ListItem><ListIcon as={FiCheckCircle} color="green.400" />OpenAI — <Code fontSize="xs">functions.config().openai.api_key</Code></ListItem>
          </List>
        </Box>

        <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={5}>
          <Heading size="sm" mb={3}>3. How it runs</Heading>
          <Text fontSize="sm" color={subtle}>
            The <Code fontSize="xs">agentsDispatcher</Code> cron ticks every 5 minutes, enqueues any enabled agent whose
            schedule is due, and the <Code fontSize="xs">agentsRunWorker</Code> executes it (long scans continue across
            invocations automatically). Data lives in the service-role-only <Code fontSize="xs">agent_*</Code> Supabase
            tables; the portal reaches them exclusively through the SuperAdmin-gated <Code fontSize="xs">agentsManage</Code> callable.
          </Text>
          <Divider my={3} />
          <Text fontSize="xs" color={subtle}>
            Privacy: findings store only headers, a short snippet, and the classification — never full message bodies.
          </Text>
        </Box>
      </VStack>

      {showStudioAdmin && <StudioAdminPanels cardBg={cardBg} cardBorder={cardBorder} subtle={subtle} />}
    </Box>
  );
}
